import React, { useState } from 'react';
import type { SubGraph } from '../types';

interface SubGraphPanelProps {
  tree: SubGraph | null;
  currentGraphId: string;
  onNavigate: (graphId: string) => void;
}

const SubGraphPanel: React.FC<SubGraphPanelProps> = ({
  tree,
  currentGraphId,
  onNavigate,
}) => {
  if (!tree) return null;

  return (
    <div
      style={{
        width: 220,
        minWidth: 220,
        background: '#fff',
        borderRight: '1px solid #e8e8e8',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transform: 'none',
        WebkitTransform: 'none',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          transform: 'none',
          WebkitTransform: 'none',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1890ff" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#262626' }}>
          图层级
        </span>
      </div>

      {/* Tree */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '8px 0',
          transform: 'none',
          WebkitTransform: 'none',
        }}
      >
        <TreeNode
          node={tree}
          currentGraphId={currentGraphId}
          onNavigate={onNavigate}
          depth={0}
        />
      </div>
    </div>
  );
};

/* ---- 树节点 ---- */

interface TreeNodeProps {
  node: SubGraph;
  currentGraphId: string;
  onNavigate: (graphId: string) => void;
  depth: number;
}

const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  currentGraphId,
  onNavigate,
  depth,
}) => {
  const [expanded, setExpanded] = useState(true);
  const isCurrent = node.id === currentGraphId;
  const hasChildren = node.children.length > 0;

  const iconColor = node.id === 'root'
    ? '#1890ff'
    : node.nodeType === 'process'
    ? '#3BC3FF'
    : '#52c41a';

  return (
    <div style={{ transform: 'none', WebkitTransform: 'none' }}>
      {/* Node row */}
      <div
        onClick={(e) => {
          e.stopPropagation();
          if (hasChildren) setExpanded(v => !v);
          if (node.id !== currentGraphId) {
            onNavigate(node.id);
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          paddingLeft: 12 + depth * 16,
          cursor: 'pointer',
          background: isCurrent ? '#e6f7ff' : 'transparent',
          borderLeft: isCurrent ? '3px solid #1890ff' : '3px solid transparent',
          transition: 'all 0.15s ease',
          userSelect: 'none',
          transform: 'none',
          WebkitTransform: 'none',
        }}
        onMouseEnter={(e) => {
          if (!isCurrent) e.currentTarget.style.background = '#f5f5f5';
        }}
        onMouseLeave={(e) => {
          if (!isCurrent) e.currentTarget.style.background = 'transparent';
        }}
      >
        {/* Expand/collapse arrow */}
        <span
          style={{
            width: 14,
            height: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            color: '#8c8c8c',
            transform: 'none',
            WebkitTransform: 'none',
            transition: 'transform 0.15s ease',
            visibility: hasChildren ? 'visible' : 'hidden',
          }}
        >
          ▶
        </span>

        {/* Type icon */}
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: node.nodeType === 'process' ? '50%' : 2,
            background: iconColor,
            flexShrink: 0,
            transform: 'none',
            WebkitTransform: 'none',
          }}
        />

        {/* Name */}
        <span
          style={{
            fontSize: 12,
            fontWeight: isCurrent ? 600 : 400,
            color: isCurrent ? '#1890ff' : '#434343',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            transform: 'none',
            WebkitTransform: 'none',
          }}
          title={node.name}
        >
          {node.name}
        </span>

        {/* Child count badge */}
        {hasChildren && (
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 10,
              color: '#8c8c8c',
              background: '#f5f5f5',
              borderRadius: 8,
              padding: '1px 6px',
              lineHeight: '16px',
              transform: 'none',
              WebkitTransform: 'none',
            }}
          >
            {node.children.length}
          </span>
        )}
      </div>

      {/* Children */}
      {expanded && hasChildren && (
        <div style={{ transform: 'none', WebkitTransform: 'none' }}>
          {node.children.map(child => (
            <TreeNode
              key={child.id}
              node={child}
              currentGraphId={currentGraphId}
              onNavigate={onNavigate}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SubGraphPanel;